import Image from 'next/image'
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

interface OutstandingResultsData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  list?: {
    site_content_id: {
      id: string;
      title: string;
      text: string;
    };
  }[];
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
}

export const fields = [
  "heading.text",
  "heading.title",
  "text.text",
  "button.label",
  "button.href",
  
  "list.site_content_id.id",
  "list.site_content_id.title",
  "list.site_content_id.text",

  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
] as const;

interface OutstandingResultsProps {
  data?: OutstandingResultsData;
}

export default function OutstandingResults({ data }: OutstandingResultsProps) {
  return (
    <section className="relative w-full overflow-hidden bg-[#F6F6F6]">
      {/* Desktop and Mobile Hero Section */}
      <div className="mx-auto py-8 xl:py-16">

        <div className="relative lg:flex lg:items-center">
          {/* Image Container */}
          <div className="relative w-full lg:w-1/2">
            <div className="relative h-[30.5625rem] lg:h-[40rem] overflow-hidden">
              <div className="absolute left-0 w-full h-full lg:rounded-tr-[25rem] lg:rounded-br-[25rem] overflow-hidden">
                {data?.image?.image_file?.image?.id && (
                  <Image
                    src={ data.image.image_file.image.id && data.image.image_file.image.filename_download ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download) : "/images/award.png"}
                    alt={data?.image?.alt || ""}
                    fill
                    className="object-cover object-center"
                    priority
                    sizes="(max-width: 768px) 100vw, 50vw"
                    quality={90}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Content Container */}
          <div className="relative px-5 lg:px-0 lg:w-1/2 lg:pl-20 lg:pr-28 z-10 -mt-[4rem] lg:mt-0">
            <div className="absolute -top-0 left-0 w-full lg:hidden -z-10">
              <Image
                src="/images/top-ellipse.svg"
                alt="Decorative top shape"
                width={393}
                height={39}
                className="w-full h-[5rem] object-cover"
                quality={90}
              />
            </div>

            <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mt-8 pt-16 lg:pt-0">
              {data?.heading?.title}
            </h2>

            <div 
              className="text-xl text-primary font-greycliff mt-8 leading-[1.35] tracking-tight prose prose-p:text-xl prose-p:text-primary prose-p:font-greycliff prose-p:leading-[1.35] prose-p:tracking-tight" 
              dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")} 
            />

            {/* Bullet Points */}
            <ul className="space-y-6 mt-8">
              {data?.list?.map((item, index) => (
                <li key={item.site_content_id.id || index} className="flex items-start gap-4">
                  <div className="w-2.5 h-2.5 mt-3 bg-primary rounded-tr-[20px] rounded-br-[20px]" />
                  <div 
                    className="flex-1 text-xl text-primary leading-relaxed prose prose-p:text-xl prose-p:text-primary prose-p:leading-relaxed" 
                    dangerouslySetInnerHTML={sanitizeHTML(item.site_content_id.title || item.site_content_id.text || "")} 
                  />
                </li>
              ))}
            </ul>

            {data?.button?.label && (
              <a 
                href={data.button.href || "#"}
                className="inline-block mt-6 lg:mt-11 w-full lg:w-auto px-12 py-4 bg-primary text-white rounded-full font-greycliff text-xl tracking-tight hover:bg-light hover:text-primary hover:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transition-all text-center"
              >
                {data.button.label}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  )
} 